import React from 'react';
import MainPrintLogo from './MainPrintLogo';
import { printPalette, printTypography, printManifest } from './printPalette';
import { sharedPrintStyles, basePrintContainerStyle } from './printStyles';

interface ManifestCarItem {
  blNo: string;
  consignee: string;
  chassisNo: string;
  packages: number;
  contents: string;
  weightKg: number;
  measurementM3: number;
  freight: string; // 'prepaid' or amount like '765,-'
  isRelated?: boolean;
  relationshipType?: string; // 'STC' or 'Attached'
}

interface ShipperGroup {
  shipperId: string;
  shipperName?: string;
  cars: ManifestCarItem[];
}

type ManifestType = 'freight' | 'cargo';

interface FreightManifestProps {
  companyName?: string;
  companyAddress?: string;
  vesselName: string;
  date: string;
  voyageNo: string;
  fromPort: string;
  toPort: string;
  shipperGroups: ShipperGroup[];
  pageNo?: number;
  includeRelated?: boolean;
  manifestType?: ManifestType;
}

const FreightManifest: React.FC<FreightManifestProps> = ({
  companyName = 'CLAPOTIS CARRIERS LTD',
  companyAddress = 'Trust Company Complex, Ajeltake Road, Majuro, Marshall Islands',
  vesselName,
  date,
  voyageNo,
  fromPort,
  toPort,
  shipperGroups,
  pageNo = 1,
  includeRelated = true,
  manifestType = 'freight',
}) => {
  const isCargo = manifestType === 'cargo';
  const colCount = isCargo ? 7 : 8;
  // Calculate totals - only count non-related cars
  const allCars = shipperGroups.flatMap(g => g.cars);
  const mainCars = allCars.filter(c => !c.isRelated);
  const totalCount = mainCars.length;
  const totalWeight = allCars.reduce((sum, c) => sum + c.weightKg, 0);
  const totalMeasurement = allCars.reduce((sum, c) => sum + c.measurementM3, 0);
  const prepaidCount = allCars.filter(c => c.freight.toLowerCase() === 'prepaid').length;
  const collectCount = totalCount - prepaidCount;
  const collectTotal = allCars
    .filter(c => c.freight.toLowerCase() !== 'prepaid')
    .reduce((sum, c) => {
      const match = c.freight.match(/[\d.,]+/);
      return sum + (match ? parseFloat(match[0].replace(',', '.')) : 0);
    }, 0);

  const formatWeight = (w: number) => w.toLocaleString('en-US', { minimumFractionDigits: 0 });
  const formatMeasurement = (m: number) => `${m.toLocaleString('en-US', { minimumFractionDigits: 1 })} m³`;
  const formatFreight = (freight: string) => {
    if (freight.toLowerCase() === 'prepaid') return freight;
    // Remove ",- " pattern from prices
    return freight.replace(/,-\s*/g, ' ');
  };

  return (
    <>
      <style>{manifestPrintStyles}</style>
      <div className="freight-manifest" style={styles.container}>
        {/* Header */}
        <MainPrintLogo companyName={companyName} companyAddress={companyAddress} />

        {/* Title row */}
        <div style={styles.titleRow}>
          <h2 style={styles.title}>{isCargo ? 'CARGO MANIFEST' : 'FREIGHT MANIFEST'}</h2>
          <span style={styles.pageNo}>Page No.: {pageNo}</span>
        </div>

        {/* Voyage info */}
        <div style={styles.voyageInfo}>
          <span>Shipped by vessel: <strong>{vesselName}</strong></span>
          <span>Date: <strong>{date}</strong></span>
          <span>Voy. No.: <strong>{voyageNo}</strong></span>
          <span>from <strong>{fromPort}</strong></span>
          <span>to <strong>{toPort}</strong></span>
        </div>

        {/* Table */}
        <table className="manifest-table" style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: isCargo ? '11%' : '10%' }}>B/L No.</th>
              <th style={{ ...styles.th, width: isCargo ? '25%' : '22%' }}>Consignee</th>
              <th style={{ ...styles.th, width: isCargo ? '15%' : '14%' }}>Chassis No.</th>
              <th style={{ ...styles.th, width: isCargo ? '8%' : '8%', textAlign: 'center' }}>Packages</th>
              <th style={{ ...styles.th, width: isCargo ? '20%' : '18%' }}>Description</th>
              <th style={{ ...styles.th, width: isCargo ? '10%' : '10%', textAlign: 'right' }}>Weight kg</th>
              <th style={{ ...styles.th, width: isCargo ? '11%' : '10%', textAlign: 'right' }}>Measurem.</th>
              {!isCargo && <th style={{ ...styles.th, width: '8%', textAlign: 'right' }}>FREIGHT</th>}
            </tr>
          </thead>
          <tbody>
            {shipperGroups.map((group, gIdx) => {
              const groupWeight = group.cars.reduce((s, c) => s + c.weightKg, 0);
              const groupMeasurement = group.cars.reduce((s, c) => s + c.measurementM3, 0);
              const groupCollect = group.cars
                .filter(c => c.freight.toLowerCase() !== 'prepaid')
                .reduce((s, c) => {
                  const match = c.freight.match(/[\d.,]+/);
                  return s + (match ? parseFloat(match[0].replace(',', '.')) : 0);
                }, 0);

              // Filter cars based on includeRelated
              const displayCars = includeRelated ? group.cars : group.cars.filter(c => !c.isRelated);
              const groupMainCars = group.cars.filter(c => !c.isRelated);

              return (
                <React.Fragment key={gIdx}>
                  {/* Shipper header */}
                  <tr className="shipper-header">
                    <td colSpan={colCount} style={styles.shipperHeader}>
                      <span style={styles.shipperId}>{group.shipperId}</span>
                      <span style={styles.shipperLabel}>SH: {group.shipperName || ''}</span>
                    </td>
                  </tr>
                  {/* Car rows */}
                  {displayCars.map((car, cIdx) => (
                    <tr key={cIdx} className="table-row" style={car.isRelated ? { backgroundColor: printPalette.rowAltBg } : undefined}>
                      <td style={styles.td}>{car.isRelated ? '' : car.blNo}</td>
                      <td style={styles.td}>{car.isRelated ? '' : `CO: ${car.consignee}`}</td>
                      <td style={{ ...styles.td, ...(car.isRelated ? { fontStyle: 'italic' } : {}) }}>
                        {car.isRelated ? `↳ ${car.chassisNo}` : `CHN: ${car.chassisNo}`}
                      </td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>{car.isRelated ? '' : car.packages}</td>
                      <td style={{ ...styles.td, ...(car.isRelated ? { fontStyle: 'italic' } : {}) }}>
                        {car.isRelated ? (
                          <>
                            {car.contents} <span style={{ color: printPalette.textMuted, fontSize: '9px' }}>({car.relationshipType})</span>
                          </>
                        ) : car.contents}
                      </td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>{car.isRelated ? '' : formatWeight(car.weightKg)}</td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>{car.isRelated ? '' : formatMeasurement(car.measurementM3)}</td>
                      {!isCargo && <td style={{ ...styles.td, textAlign: 'right' }}>{car.isRelated ? '' : formatFreight(car.freight)}</td>}
                    </tr>
                  ))}
                  {/* Subtotal */}
                  <tr className="subtotal-row">
                    <td colSpan={3} style={styles.subtotalLabel}>Subtotal {group.shipperName || group.shipperId}:</td>
                    <td style={{ ...styles.subtotalValue, textAlign: 'center' }}>{groupMainCars.length}</td>
                    <td style={styles.subtotalValue}></td>
                    <td style={{ ...styles.subtotalValue, textAlign: 'right' }}>{formatWeight(groupWeight)}</td>
                    <td style={{ ...styles.subtotalValue, textAlign: 'right' }}>{formatMeasurement(groupMeasurement)}</td>
                    {!isCargo && (
                      <td style={{ ...styles.subtotalValue, textAlign: 'right' }}>
                        {groupCollect > 0 ? `${groupCollect.toLocaleString('en-US', { minimumFractionDigits: 0 })} EUR` : '0 EUR'}
                      </td>
                    )}
                  </tr>
                </React.Fragment>
              );
            })}
            {/* Total row */}
            <tr className="total-row">
              <td colSpan={3} style={styles.totalLabel}>Total:</td>
              <td style={{ ...styles.totalValue, textAlign: 'center' }}>{totalCount}</td>
              <td style={styles.totalValue}>
                {!isCargo && <>Pp. <strong>{prepaidCount}</strong> &nbsp;&nbsp; Col. <strong>{collectCount}</strong></>}
              </td>
              <td style={{ ...styles.totalValue, textAlign: 'right' }}>{formatWeight(totalWeight)} kg</td>
              <td style={{ ...styles.totalValue, textAlign: 'right' }}>{formatMeasurement(totalMeasurement)}</td>
              {!isCargo && (
                <td style={{ ...styles.totalValue, textAlign: 'right' }}>
                  {collectTotal > 0 ? `${collectTotal.toLocaleString('en-US', { minimumFractionDigits: 0 })} EUR` : '0 EUR'}
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
};

const manifestPrintStyles = `
  ${sharedPrintStyles}

  @media print {
    .manifest-table { page-break-inside: auto; }
    .manifest-table thead { display: table-header-group; }
    .manifest-table tbody { display: table-row-group; }
    .table-row { page-break-inside: avoid; page-break-after: auto; }
    .shipper-header { page-break-after: avoid; }
    .subtotal-row { page-break-before: avoid; }
  }
`;

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    ...basePrintContainerStyle,
    padding: printManifest.containerPadding,
    fontSize: `${printTypography.baseSize}px`,
    border: `${printManifest.containerBorder} solid ${printPalette.primaryDark}`,
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
  },
  title: {
    fontSize: `${printTypography.titleSize}px`,
    fontWeight: 'bold',
    margin: 0,
    color: printPalette.primaryDark,
  },
  pageNo: {
    fontSize: `${printTypography.infoSize}px`,
    color: printPalette.text,
  },
  voyageInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    marginBottom: '15px',
    fontSize: `${printTypography.infoSize}px`,
    color: printPalette.text,
    borderBottom: `1px solid ${printPalette.borderLight}`,
    paddingBottom: '10px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: `${printTypography.tableSize}px`,
    color: printPalette.text,
    tableLayout: 'fixed',
  },
  th: {
    border: `${printManifest.cellBorder} solid ${printPalette.primaryDark}`,
    borderTop: `${printManifest.headerBorder} solid ${printPalette.primaryDark}`,
    borderBottom: `${printManifest.headerBorder} solid ${printPalette.primaryDark}`,
    padding: `${printManifest.headerPadding} 6px`,
    textAlign: 'left',
    fontWeight: 'bold',
    color: printPalette.primaryDark,
    backgroundColor: printPalette.headerBg,
  },
  shipperHeader: {
    padding: `${printManifest.shipperHeaderPadding} 6px`,
    border: `${printManifest.cellBorder} solid ${printPalette.borderLight}`,
    backgroundColor: printManifest.shipperHeaderBg,
    fontWeight: 'bold',
    color: printPalette.text,
  },
  shipperId: {
    marginRight: '20px',
  },
  shipperLabel: {
    fontWeight: 'normal',
  },
  td: {
    padding: printManifest.cellPadding,
    border: `${printManifest.cellBorder} solid ${printPalette.borderLight}`,
    color: printPalette.text,
    verticalAlign: 'top',
  },
  subtotalLabel: {
    padding: `${printManifest.subtotalPadding} 6px`,
    border: `${printManifest.cellBorder} solid ${printPalette.secondary}`,
    borderTop: `${printManifest.subtotalBorder} solid ${printPalette.secondary}`,
    borderBottom: `${printManifest.subtotalBorder} solid ${printPalette.secondary}`,
    fontWeight: 'bold',
    color: printPalette.text,
    backgroundColor: printPalette.headerBg,
  },
  subtotalValue: {
    padding: `${printManifest.subtotalPadding} 6px`,
    border: `${printManifest.cellBorder} solid ${printPalette.secondary}`,
    borderTop: `${printManifest.subtotalBorder} solid ${printPalette.secondary}`,
    borderBottom: `${printManifest.subtotalBorder} solid ${printPalette.secondary}`,
    fontWeight: 'bold',
    color: printPalette.text,
    backgroundColor: printPalette.headerBg,
  },
  totalLabel: {
    padding: `${printManifest.totalPadding} 6px`,
    border: `${printManifest.headerBorder} solid ${printPalette.primaryDark}`,
    fontWeight: 'bold',
    color: printPalette.primaryDark,
    backgroundColor: printPalette.headerBg,
  },
  totalValue: {
    padding: `${printManifest.totalPadding} 6px`,
    border: `${printManifest.headerBorder} solid ${printPalette.primaryDark}`,
    fontWeight: 'bold',
    color: printPalette.primaryDark,
    backgroundColor: printPalette.headerBg,
  },
};

export default FreightManifest;

